import { ElementStyle } from '@basis-theory/react-elements'
import React from 'react'

import { BaseField } from '@ui/components/Input/BaseField/BaseField'
import { IconFCType } from '@ui/icons/types'

import { detectThemeClientSide } from '@global/theme/Theme'

import styles from './BtField.module.scss'

interface BtFieldProps {
  onClick?: () => void
  placeholder?: string
  hasValue?: boolean
  isFocused?: boolean
  label: string
  rightIcon?: IconFCType
  children: React.ReactNode
}

export const BtField: React.FC<BtFieldProps> = ({
  onClick,
  rightIcon,
  children,
  hasValue = false,
  isFocused = false,
  placeholder,
  label,
}) => {
  const theme = detectThemeClientSide()
  const isDarkMode = theme === 'dark'
  const shouldFloatLabel = hasValue || isFocused

  const inputStyles: ElementStyle = {
    fonts: [
      'https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&display=swap',
    ],
    base: {
      color: isDarkMode ? '#F9F9FA' : '#0A0C11',
      backgroundColor: 'transparent',
      fontFamily: '"Inter", sans-serif',
      fontSize: '16px',
      lineHeight: '24px',
      padding: '0',
      '::placeholder': { color: isDarkMode ? '#EBECF0' : '#5B616D' },
    },
    invalid: {
      color: isDarkMode ? '#F36363' : '#F03D3D',
      backgroundColor: 'transparent',
      '::placeholder': { color: isDarkMode ? '#8F95A3' : '#5B616D' },
    },
    empty: {
      color: isDarkMode ? '#F9F9FA' : '#0A0C11',
      backgroundColor: 'transparent',
      '::placeholder': { color: isDarkMode ? '#8F95A3' : '#5B616D' },
    },
    complete: {
      color: isDarkMode ? '#B8A8FF' : '#7251FF',
      backgroundColor: 'transparent',
    },
  }

  const childrenWithStyle = React.Children.map(children, (child) => {
    if (!React.isValidElement(child)) return child
    return React.cloneElement(
      child as React.ReactElement<{ style: ElementStyle; placeholder: string }>,
      {
        style: inputStyles,
        placeholder: '',
      }
    )
  })

  return (
    <div className={styles.btField} onClick={onClick}>
      <BaseField
        label={label}
        placeholder={placeholder ?? label}
        error={false}
        focused={isFocused}
        rightIcon={rightIcon}
        isLabelActingAsPlaceholder={!shouldFloatLabel}
      >
        {childrenWithStyle}
      </BaseField>
    </div>
  )
}
